import React from 'react';
import { MoonshotBox } from './MoonshotBox';
import { MoonshotSteps } from 'app/common/data/campaigns';
import { MoonshotSubmitFunction } from './types';
import styles from './MoonshotPlanSelectionFreeTrial.less';
import { Icon, IconName } from 'app/common/components/controls';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forNamespace } from 'app/common/lib/util/i18n';

const format = forNamespace(['moonshot', 'free trial offer']);

interface MoonshotPlanSelectionProps {
  onSubmit: MoonshotSubmitFunction;
}

const features = [
  {
    icon: IconName.Board,
    title: format('unlimited boards heading'),
    description: format('unlimited boards description'),
  },
  {
    icon: IconName.PowerUp,
    title: format('unlimited integrations heading'),
    description: format('unlimited integrations description'),
  },
  {
    icon: IconName.ButlerBot,
    title: format('automations heading'),
    description: format('automations description'),
  },
  {
    icon: IconName.Admin,
    title: format('permissions heading'),
    description: format('permissions description'),
  },
  {
    icon: IconName.Heart,
    title: format('priority support heading'),
    description: format('priority support description'),
  },
  {
    icon: IconName.Vote,
    title: format('more features heading'),
    description: format('more features description'),
  },
];

export const MoonshotPlanSelectionFreeTrial: React.FC<MoonshotPlanSelectionProps> = ({
  onSubmit,
}) => {
  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  const onContinueWithTracking = () => {
    onSubmit(MoonshotSteps.PAYMENT_FORM);
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SUBMITS,
      directObj: Noun.BUSINESS_CLASS_FREE_TRIAL_UPSELL,
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'submitted',
        actionSubject: 'upsell',
        actionSubjectId: 'bcFreeTrialUpsell',
        source: 'moonshotBCFreeTrialUpsellScreen',
      });
    }
  };

  const onSkipWithTracking = () => {
    onSubmit(MoonshotSteps.SUCCESS);
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.SKIPS,
      directObj: Noun.BUSINESS_CLASS_FREE_TRIAL_UPSELL,
    });

    if (sendGASEvent) {
      Analytics.sendTrackEvent({
        action: 'skipped',
        actionSubject: 'upsell',
        actionSubjectId: 'bcFreeTrialUpsell',
        source: 'moonshotBCFreeTrialUpsellScreen',
      });
    }
  };

  return (
    <>
      <MoonshotBox className={styles.upperBox}>
        <div className={styles.content}>
          <div className={styles.exclusiveOffer}>
            <img
              src={require('resources/images/moonshot/exclusive-offer.png')}
              alt=""
            />
            <span>{format('exclusive offer image')}</span>
          </div>
          <h1>
            <img
              src={require('resources/images/moonshot/bc-sparkle.svg')}
              alt=""
            />
            {format('free trial heading')}
          </h1>
          <p>{format('free trial messaging')}</p>
          <div className={styles.features}>
            {features.map(({ icon, title, description }, i) => (
              <div className={styles.feature} key={`feature-${i}`}>
                <Icon name={icon} small className={styles.featureIcon} />
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
          <button
            className={styles.startTrialButton}
            onClick={onContinueWithTracking}
          >
            {format('start free trial button')}
          </button>
          <p className={styles.quiet}>{format('downgrade messaging')}</p>
        </div>
        <div className={styles.trustedBy}>
          <span>{format('companies that use business class messaging')}</span>
          <img
            src={require('resources/images/moonshot/fender.png')}
            alt="Fender"
            width="45"
          />
          <img
            src={require('resources/images/moonshot/the-new-york-times.png')}
            alt="The New York Times"
            width="137"
          />
          <img
            src={require('resources/images/moonshot/fortnite.png')}
            alt="Fortnite"
            width="58"
          />
        </div>
      </MoonshotBox>
      <button className={styles.startFreeButton} onClick={onSkipWithTracking}>
        {format('skip free trial button')}
      </button>
    </>
  );
};
